import React from 'react'
import styled from 'styled-components'
import { GhostButton, PrimaryButton, Row } from '../../../layout/styledComponents'

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${(p) => p.theme?.color?.overlay || 'rgba(31,41,55,0.55)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${(p) => p.theme?.z?.modal || 60};
  padding: 24px;
  /* On mobile, drop the inset and bottom-align so the dialog reads as
     anchored to the bottom edge — sheet feel rather than tiny window. */
  @media (max-width: 540px) {
    padding: 12px;
    align-items: flex-end;
  }
`

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  /* Cap height so a long form (e.g. FoodLogDialog with many selected
     entries + suggestion list + photos) never grows past the viewport.
     Header + footer stay pinned; the body section scrolls. */
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border-radius: 20px;
  box-shadow: ${(p) => p.theme?.shadow?.raised};
  overflow: hidden;
  @media (max-width: 540px) {
    max-height: calc(100vh - 24px);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`

const Header = styled.header`
  flex-shrink: 0;
  padding: 28px 24px 16px;
`

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const Sub = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  line-height: 1.5;
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 24px 20px;
  /* Soft scrollbar to match the calm clinical theme. */
  scrollbar-width: thin;
  scrollbar-color: ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'} transparent;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'};
    border-radius: 999px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const Footer = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${(p) => p.theme?.color?.borderSoft || 'rgba(31,41,55,0.06)'};
  padding: 14px 24px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
`

/**
 * Lightweight dialog shell for the patient portal. Avoids pulling in another
 * MUI Dialog given the patient portal's calmer visual register; we already
 * have the styled-components theme.
 *
 * Layout: flex column with a fixed header (title + subtitle), a flexible
 * body that scrolls when content overflows, and a footer with the action
 * buttons pinned to the bottom. The whole sheet is capped at viewport
 * height minus padding so a tall form (e.g. {@code FoodLogDialog} with
 * many selected items + suggestions + photos) is never cut off — the user
 * scrolls the body, action buttons stay reachable.
 */
export default function SessionDialog({
  open,
  title,
  subtitle,
  primaryLabel,
  onPrimary,
  primaryTone = 'primary', // 'primary' | 'urgent'
  primaryDisabled,
  primaryLoading,
  onClose,
  children,
}) {
  if (!open) return null
  return (
    <Backdrop onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          {subtitle && <Sub>{subtitle}</Sub>}
        </Header>
        <Body>{children}</Body>
        <Footer>
          <Row $gap="10px" $wrap style={{ justifyContent: 'flex-end' }}>
            <GhostButton onClick={onClose} disabled={primaryLoading}>
              Cancel
            </GhostButton>
            {onPrimary && (
              <PrimaryButton
                onClick={onPrimary}
                disabled={primaryDisabled || primaryLoading}
                style={
                  primaryTone === 'urgent'
                    ? { background: '#C24D49' }
                    : undefined
                }
              >
                {primaryLoading ? 'Working…' : primaryLabel}
              </PrimaryButton>
            )}
          </Row>
        </Footer>
      </Sheet>
    </Backdrop>
  )
}
